import type { ReactNode } from "react";
import { useState } from "react";
import { usePortal } from "../app/context.js";
import { href } from "../app/router.js";
import { SurveySharePanel } from "./SurveySharePanel.jsx";

/**
 * Everything this survey can be turned into, in one place.
 *
 * They were scattered: the hand-off bundle, the JSON and the photographs on
 * the survey's own header, the plan's own formats inside the drawing's Export
 * menu, the room schedule beside it, the installer pack on a screen of its
 * own. Six places, and no screen that answered "what can I send somebody".
 *
 * ORDERED BY WHO IS WAITING. The hand-off is first because it is the one with
 * somebody at the other end — a designer who cannot start until it arrives.
 * The rest are errands: a file for a customer, an assessor, a colleague.
 *
 * WHAT A REFUSAL MEANS IS SAID IN FULL. `buildSurveyIngestExport` refuses a
 * survey with no address, and the API's reason is the useful sentence, so it
 * is shown rather than replaced with "Export failed".
 */

type Kind = "handoff" | "json" | "photos" | "plan-pdf" | "plan-dxf" | "schedule-pdf" | "schedule-csv";

/** Saves a downloaded blob under its own name, then lets the object URL go. */
function save(file: { url: string; filename: string }): void {
  const a = document.createElement("a");
  a.href = file.url;
  a.download = file.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Long enough for the browser to take the bytes; then release it.
  setTimeout(() => URL.revokeObjectURL(file.url), 60_000);
}

export function SurveyExports({
  surveyId,
  jobId,
  hasAddress,
}: {
  readonly surveyId: string;
  readonly jobId: string;
  /** False when the hand-off would be refused, which is said before it is tried. */
  readonly hasAddress: boolean;
}): ReactNode {
  const { client } = usePortal();
  const [busy, setBusy] = useState<Kind | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);

  const run = (kind: Kind, get: () => Promise<{ url: string; filename: string }>): void => {
    setBusy(kind);
    setError(null);
    get()
      .then(save)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : "That file could not be made."),
      )
      .finally(() => setBusy(null));
  };

  const label = (kind: Kind, idle: string, working: string): string =>
    busy === kind ? working : idle;

  return (
    <section className="exports" data-testid="survey-exports">
      <h2 className="section-head">Send or download</h2>

      {error !== null ? (
        <p className="notice fail" role="alert" data-testid="export-error">
          {error}
        </p>
      ) : null}

      <div className="exports__grid">
        {/* First, because somebody is waiting at the other end of it. */}
        <div className="export-card export-card--lead">
          <p className="export-card__title">Hand it to the designer</p>
          <p className="export-card__note">
            The agreed document, the scan and the photographs, as one file.
          </p>
          {hasAddress ? null : (
            <p className="export-card__blocked" data-testid="handoff-blocked">
              Not yet: the designer&rsquo;s portal files a property by its address, and this
              survey has none.
            </p>
          )}
          <button
            type="button"
            className="primary"
            disabled={!hasAddress || busy !== null}
            onClick={() => run("handoff", () => client.surveyIngestExport(surveyId, "zip"))}
          >
            {label("handoff", "Make the hand-off file", "Bundling…")}
          </button>
          {/*
            THE HANDOVER CODE, which had no button on this screen at all.
            portal.scannix.co's "Import from Scannix" asks for a code and pulls
            the whole pack itself -- and the only way to get one was the Share
            button on the drawing's own toolbar, two doors away. The first
            person to look for it minted the STANDING feed credential instead,
            pasted that, and was told the code was invalid. Both are real and
            they are not interchangeable, so both are offered here, where the
            question is actually asked, and each says which it is.
          */}
          <div className="export-card__row">
            <button
              type="button"
              className="secondary"
              aria-expanded={sharing}
              aria-controls="survey-handover-code"
              onClick={() => setSharing((open) => !open)}
            >
              {sharing ? "Hide the handover code" : "Get the handover code"}
            </button>
          </div>
          <p className="export-card__note">
            A code for <strong>one survey</strong>, pasted into
            portal.scannix.co &rarr; Import from Scannix. Or{" "}
            <a href={href.handoff()}>connect portal.scannix.co once</a> and it
            collects every survey itself, with no code to paste.
          </p>
        </div>

        <div className="export-card">
          <p className="export-card__title">The drawing</p>
          <p className="export-card__note">Every floor on A3, or the lines for a CAD program.</p>
          <div className="export-card__row">
            <button
              type="button"
              className="secondary"
              disabled={busy !== null}
              onClick={() => run("plan-pdf", () => client.planExport(surveyId, "pdf"))}
            >
              {label("plan-pdf", "PDF", "Drawing…")}
            </button>
            <button
              type="button"
              className="secondary"
              disabled={busy !== null}
              onClick={() => run("plan-dxf", () => client.planExport(surveyId, "dxf"))}
            >
              {label("plan-dxf", "DXF", "Drawing…")}
            </button>
          </div>
        </div>

        <div className="export-card">
          <p className="export-card__title">The room schedule</p>
          <p className="export-card__note">Every room, its sizes and where each figure came from.</p>
          <div className="export-card__row">
            <button
              type="button"
              className="secondary"
              disabled={busy !== null}
              onClick={() => run("schedule-pdf", () => client.roomSchedule(surveyId, "pdf"))}
            >
              {label("schedule-pdf", "PDF", "Making…")}
            </button>
            <button
              type="button"
              className="secondary"
              disabled={busy !== null}
              onClick={() => run("schedule-csv", () => client.roomSchedule(surveyId, "csv"))}
            >
              {label("schedule-csv", "CSV", "Making…")}
            </button>
          </div>
        </div>

        <div className="export-card">
          <p className="export-card__title">The photographs</p>
          <p className="export-card__note">
            On their own, for a customer or an assessor — no survey file to explain.
          </p>
          <button
            type="button"
            className="secondary"
            disabled={busy !== null}
            onClick={() => run("photos", () => client.surveyPhotosExport(surveyId))}
          >
            {label("photos", "Download the photos", "Bundling…")}
          </button>
        </div>

        <div className="export-card">
          <p className="export-card__title">The whole survey as data</p>
          <p className="export-card__note">
            The same model the PDF is drawn from, as JSON, for another system.
          </p>
          <button
            type="button"
            className="secondary"
            disabled={busy !== null}
            onClick={() => run("json", () => client.surveyModelJson(surveyId))}
          >
            {label("json", "Download JSON", "Preparing…")}
          </button>
        </div>
      </div>

      {sharing ? (
        <div id="survey-handover-code">
          <SurveySharePanel
            surveyId={surveyId}
            jobId={jobId}
            onClose={() => setSharing(false)}
          />
        </div>
      ) : null}
    </section>
  );
}
